import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import InlineFeedPost from '../components/InlineFeedPost'
import PostModal from '../components/PostModal'
import { ROLE_LABELS } from '../lib/supabase'
import RankBadge from '../components/RankBadge'
import ProfileCard from '../components/ProfileCard'
import ReadTheirWork from '../components/ReadTheirWork'

export default function WriterProfilePage() {
  const { id } = useParams()
  const [writer, setWriter] = useState(null)
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedPostId, setSelectedPostId] = useState(null) // kept for future use

  useEffect(() => {
    async function load() {
      const [profileResult, articlesResult] = await Promise.all([
        supabase.from('profiles').select('*, press_theme').eq('id', id).maybeSingle(),
        supabase.from('posts').select('*, profiles!posts_author_id_fkey(id, display_name, column_inches, is_voice)')
          .eq('author_id', id).eq('status', 'published')
          .order('created_at', { ascending: false })
      ])
      setWriter(profileResult.data || null)
      setPosts(articlesResult.data || [])
      setLoading(false)
    }
    load()
  }, [id])

  if (loading) return <div style={styles.page}><div style={styles.loading}>Loading...</div></div>
  if (!writer) return <div style={styles.page}><div style={styles.loading}>Writer not found.</div></div>

  const roleColor = { viewer: 'var(--muted)', writer_blog: '#4a90d9', writer_all: '#7b5ea7', mod: 'var(--warning)', admin: 'var(--accent)' }

  return (
    <div style={styles.page}>
      <div style={{ paddingTop: '4.5rem' }}>
        <ProfileCard
          displayName={writer.display_name}
          role={writer.role}
          bio={writer.bio}
          columnInches={writer.column_inches || 0}
          isVoice={writer.is_voice || false}
          isFounding={writer.is_founding || false}
          pressTheme={writer.press_theme || 'default'}
          size="profile"
          postCount={posts.length}
        >
          <ReadTheirWork writerId={id} writerName={writer?.display_name} />
        </ProfileCard>
      </div>

      <div className="writer-body">
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          <Link to={`/record/${id}`} className="btn btn-ghost btn-sm">◈ View The Record</Link>
        </div>
        <h2 style={styles.sectionTitle}>All Pieces</h2>
        {posts.length === 0 ? (
          <div className="empty-state"><p>No published pieces yet.</p></div>
        ) : (
          <div>
            {posts.map((post, i) => (
              <InlineFeedPost key={post.id} post={post} onOpen={id => setSelectedPostId(id)} />
            ))}
          </div>
        )}
      </div>

      {selectedPostId && (
        <PostModal
          postId={selectedPostId}
          onClose={() => setSelectedPostId(null)}
          onNavigateWriter={id => { setSelectedPostId(null); window.location.href = `/writer/${id}` }}
        />
      )}
    </div>
  )
}

const styles = {
  page: { paddingTop: '4.5rem', minHeight: '100vh' },
  loading: { padding: '6rem 3rem', fontFamily: "'DM Mono', monospace", fontSize: '0.75rem', color: 'var(--muted)' },
  header: {
    padding: '4rem 3rem', borderBottom: '1px solid var(--rule)',
    background: 'var(--cream)', display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap',
  },
  avatar: {
    width: 72, height: 72, background: 'var(--accent)', color: '#F0EBE3',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1.8rem', fontWeight: 700, borderRadius: '50%', flexShrink: 0,
  },
  writerInfo: { flex: 1 },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.4rem' },
  name: { fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '0.5rem' },
  statBlock: { textAlign: 'right' },
  stat: {},
  statNum: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '2.5rem', fontWeight: 700, lineHeight: 1, color: 'var(--accent)' },
  statLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)' },
  body: { padding: '3rem' },
  sectionTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.4rem', fontWeight: 700, marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '1px solid var(--rule)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', border: '1px solid var(--rule)' },
}
